/** Europe/Helsinki: weekly full sync window and week identity. */
const HELSINKI_TZ = "Europe/Helsinki";

/**
 * Entry shape for `article-list.json`. The manifest accepts either a plain
 * filename string (legacy) or an object with an optional `dateModified` token
 * that triggers per-entry cache invalidation when it changes.
 */
export interface ArticleListEntry {
  filename: string;
  dateModified?: string;
}

export const ARTICLE_INCREMENTAL_BODY_COUNT = 4;

export const ARTICLES_LAST_FULL_SYNC_WEEK_ID_KEY = "articlesLastFullSyncWeekId";

/** Debounce between visibility-driven syncs (ms). */
export const ARTICLES_VISIBILITY_SYNC_DEBOUNCE_MS = 3 * 60 * 1000;

export const ARTICLES_LAST_LIGHT_SYNC_AT_KEY = "articlesLastLightSyncAt";

/**
 * When the remote filename list matches persisted list, do not refetch top-N
 * article bodies more often than this. When lists differ, a full refresh runs
 * on that sync regardless of this interval.
 */
export const ARTICLE_LIGHT_SYNC_MIN_INTERVAL_MS = 10 * 60 * 1000;

const getHelsinkiYmd = (date: Date): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: HELSINKI_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(date);

const getHelsinkiWeekdayShort = (date: Date): string =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: HELSINKI_TZ,
    weekday: "short"
  }).format(date);

const getHelsinkiHour = (date: Date): number => {
  const hourStr = new Intl.DateTimeFormat("en-GB", {
    timeZone: HELSINKI_TZ,
    hour: "numeric",
    hour12: false
  }).format(date);
  return Number.parseInt(hourStr, 10);
};

/**
 * Monday 06:00–11:59 (Helsinki). Used to trigger one full article sync per week
 * when a returning user opens /blog.
 */
export const isMondayMorningHelsinki = (date: Date): boolean => {
  if (getHelsinkiWeekdayShort(date) !== "Mon") {
    return false;
  }
  const hour = getHelsinkiHour(date);
  return hour >= 6 && hour < 12;
};

/**
 * YYYY-MM-DD of the Monday starting the Helsinki-local week containing `date`.
 */
export const getHelsinkiWeekMondayKey = (date: Date): string => {
  let t = date;
  for (let i = 0; i < 8; i++) {
    if (getHelsinkiWeekdayShort(t) === "Mon") {
      return getHelsinkiYmd(t);
    }
    t = new Date(t.getTime() - 24 * 60 * 60 * 1000);
  }
  return getHelsinkiYmd(date);
};

export const sortArticleFilenamesDesc = (filenames: string[]): string[] =>
  [...filenames].sort((a, b) => b.localeCompare(a, "fi"));

export const pickTopFilenames = (
  sortedDesc: string[],
  count: number
): string[] => sortedDesc.slice(0, count);

/** Articles in list order (newest filenames first); skips entries not yet loaded. */
export const articlesFromFilenameList = <T>(
  filenames: string[],
  filenameToSlug: Record<string, string>,
  articles: Record<string, T>
): T[] =>
  sortArticleFilenamesDesc(filenames)
    .map((fn) => {
      const slug = filenameToSlug[fn];
      return slug != null && slug !== "" ? articles[slug] : undefined;
    })
    .filter((a): a is T => a != null);

/**
 * Filenames whose remote `dateModified` differs from what the client previously
 * stored. Entries without `dateModified` are ignored so legacy `string[]`
 * manifests stay neutral.
 */
export const findStaleFilenamesByModified = (
  remote: ArticleListEntry[],
  stored: Record<string, string>
): string[] =>
  remote
    .filter(
      (entry) =>
        entry.dateModified != null &&
        stored[entry.filename] !== entry.dateModified
    )
    .map((entry) => entry.filename);

/** Order-insensitive equality for article list filenames from the bucket. */
export const areArticleFilenamesEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  const sa = [...a].sort((x, y) => x.localeCompare(y, "fi"));
  const sb = [...b].sort((x, y) => x.localeCompare(y, "fi"));
  for (let i = 0; i < sa.length; i++) {
    if (sa[i] !== sb[i]) {
      return false;
    }
  }
  return true;
};

export const readLastFullSyncWeekId = (): string | null => {
  try {
    return localStorage.getItem(ARTICLES_LAST_FULL_SYNC_WEEK_ID_KEY);
  } catch {
    return null;
  }
};

export const writeLastFullSyncWeekId = (weekMondayKey: string): void => {
  try {
    localStorage.setItem(ARTICLES_LAST_FULL_SYNC_WEEK_ID_KEY, weekMondayKey);
  } catch {
    /* ignore quota / private mode */
  }
};

export const readLastLightSyncAt = (): number | null => {
  try {
    const raw = localStorage.getItem(ARTICLES_LAST_LIGHT_SYNC_AT_KEY);
    if (raw == null) {
      return null;
    }
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

export const writeLastLightSyncAt = (timestampMs: number): void => {
  try {
    localStorage.setItem(ARTICLES_LAST_LIGHT_SYNC_AT_KEY, String(timestampMs));
  } catch {
    /* ignore quota / private mode */
  }
};

export interface ShouldRunWeeklyFullSyncParams {
  pathname: string;
  hasCachedArticles: boolean;
  now: Date;
}

/**
 * Returning user on /blog during Monday morning (Helsinki), at most one full
 * sync per calendar week (Monday key).
 */
export const shouldRunWeeklyFullSync = (
  params: ShouldRunWeeklyFullSyncParams
): boolean => {
  if (params.pathname !== "/blog") {
    return false;
  }
  if (!params.hasCachedArticles) {
    return false;
  }
  if (!isMondayMorningHelsinki(params.now)) {
    return false;
  }
  const currentWeekMonday = getHelsinkiWeekMondayKey(params.now);
  const last = readLastFullSyncWeekId();
  return last !== currentWeekMonday;
};
